/**
 * The roles, as people read them.
 *
 * JMC管理者 > クリニック管理者 > 店舗管理者 > スタッフ. One table, because the
 * same four words were written into eight screens separately - 「店舗ユーザー」
 * here, 「店舗ユーザー（担当店舗のみ）」 there - and a fifth screen would have
 * spelt it a sixth way.
 *
 * The values are the server's (`TenantRole` in shared/schemas/common.ts, plus
 * the platform role); only the words live here.
 */
import type { Session } from './api.ts';

export type TenantRoleName = 'CLINIC_ADMIN' | 'STORE_ADMIN' | 'STORE_USER';

/** Short names: chips, table cells, the sidebar's footer. */
export const ROLE_LABEL: Record<string, string> = {
  JMC_ADMIN: 'JMC管理者',
  CLINIC_ADMIN: 'クリニック管理者',
  STORE_ADMIN: '店舗管理者',
  STORE_USER: 'スタッフ',
  NONE: '所属なし',
};

/** What each role may do, in the words somebody granting it needs. */
export const ROLE_REACH: Record<TenantRoleName, string> = {
  CLINIC_ADMIN:
    'すべての店舗の記事・情報・ユーザーを扱えます。クリニック設定と、ユーザーの追加・停止もできます。',
  STORE_ADMIN:
    '担当店舗の記事・情報に加え、その店舗のユーザー（スタッフ・店舗管理者）の追加・停止と、店舗情報の変更ができます。',
  STORE_USER:
    '担当店舗の記事と情報だけを扱えます。ほかの店舗の記事は見えません。ユーザーの管理はできません。',
};

/** Picker options, widest first. A store admin is offered only the store roles. */
export function roleOptions(canGrantClinicAdmin: boolean) {
  return [
    ...(canGrantClinicAdmin
      ? [{ value: 'CLINIC_ADMIN', label: 'クリニック管理者（全店舗）' }]
      : []),
    { value: 'STORE_ADMIN', label: '店舗管理者（担当店舗のユーザー管理・店舗情報）' },
    { value: 'STORE_USER', label: 'スタッフ（担当店舗の記事作成）' },
  ];
}

/**
 * Who the signed-in person is in the active clinic, and which stores they may
 * manage people in. A clinic admin manages every store; a store admin the ones
 * marked `admin`; staff none.
 */
export function sessionRole(session: Session): {
  role: TenantRoleName | 'JMC_ADMIN' | null;
  manageStores: Array<{ id: string; name: string }>;
} {
  if (session.user.platformRole === 'JMC_ADMIN') return { role: 'JMC_ADMIN', manageStores: [] };
  const clinic = session.memberships.find((m) => m.clinicId === session.activeClinicId);
  if (!clinic) return { role: null, manageStores: [] };
  if (clinic.clinicAdmin) return { role: 'CLINIC_ADMIN', manageStores: clinic.stores };
  const managed = clinic.stores.filter((store) => store.admin === true);
  return { role: managed.length > 0 ? 'STORE_ADMIN' : 'STORE_USER', manageStores: managed };
}
